import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, SlidersHorizontal, Image, ChevronLeft, ChevronRight, AlertCircle, RefreshCcw } from 'lucide-react';
import { Product } from '@/types';
import { formatPrice, truncateText, getCache, setCache, getDomain } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const PRODUCTS_PER_PAGE = 12;

export function ProductsPage() {
  const location = useLocation();
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(location.state?.searchTerm || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    location.state?.selectedCategory || ''
  );
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'name'>('name');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (location.state?.searchTerm) {
      setSearchTerm(location.state.searchTerm);
    }
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
    }
  }, [location.state]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const domain = getDomain();
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/products.php?domain=${domain}`);
      
      if (!response.ok) {
        throw new Error('Failed to connect to server. Please check your connection and try again.');
      }
      
      const data = await response.json();
      if (!data.status) {
        throw new Error(data.message || 'Failed to load products. Please try again.');
      }
      
      const filteredProducts = data.products.filter((p: Product) => p.status === 1);
      setAllProducts(filteredProducts);
      setCache('products', filteredProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortBy]);

  const categories = [...new Set(allProducts.map(p => p.category))].filter(Boolean);

  const filteredProducts = allProducts
    .filter(product => 
      (product.produt_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()))) &&
      (!selectedCategory || product.category === selectedCategory)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc':
          return parseFloat(a.sellingprice) - parseFloat(b.sellingprice);
        case 'price-desc':
          return parseFloat(b.sellingprice) - parseFloat(a.sellingprice);
        default:
          return a.produt_name.localeCompare(b.produt_name);
      }
    });

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIndex = startIndex + PRODUCTS_PER_PAGE;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Products - Store</title>
        <meta name="description" content="Browse our collection of products" />
      </Helmet>

      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-4 pb-24 md:pb-4">
        <div className="mx-auto max-w-7xl">
          {/* Search and Filters */}
          <div className="sticky top-16 z-10 bg-gray-100 dark:bg-gray-900 pt-4 pb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 h-14 rounded-xl border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-brand-primary focus:border-transparent text-lg"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-6 w-6" />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <SlidersHorizontal className="h-5 w-5" />
              </button>
            </div>

            {showFilters && (
              <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full h-12 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white px-4"
                    >
                      <option value="">All Categories</option>
                      {categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort By
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                      className="w-full h-12 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-700 dark:text-white px-4"
                    >
                      <option value="name">Name</option>
                      <option value="price-asc">Price: Low to High</option>
                      <option value="price-desc">Price: High to Low</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {loading ? (
              [...Array(PRODUCTS_PER_PAGE)].map((_, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                  <div className="aspect-square bg-gray-200 dark:bg-gray-700 animate-pulse" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse" />
                  </div>
                </div>
              ))
            ) : error ? (
              <div className="col-span-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center">
                <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <Button onClick={fetchProducts} className="inline-flex items-center">
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Try Again
                </Button>
              </div>
            ) : currentProducts.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 dark:text-gray-400">No products found</p>
              </div>
            ) : (
              currentProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <div className="aspect-square relative">
                    {product.images && product.images.length > 0 ? (
                      <img
                        src={product.images[0]}
                        alt={product.produt_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <Image className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                    {product.postType === "1" && (
                      <div className="absolute top-2 right-2 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Pre-Order
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2 group-hover:text-brand-primary transition-colors line-clamp-2">
                      {truncateText(product.produt_name, 50)}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      {product.category || 'Uncategorized'}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-brand-primary dark:text-brand-light">
                        {formatPrice(product.sellingprice)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && !loading && !error && (
            <div className="mt-8 flex justify-center items-center gap-2">
              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="hidden md:flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <div className="flex items-center gap-1">
                {currentPage > 2 && (
                  <Button
                    variant={currentPage === 1 ? 'default' : 'outline'}
                    onClick={() => handlePageChange(1)}
                    className="w-10 h-10 p-0"
                  >
                    1
                  </Button>
                )}

                {currentPage > 3 && <span className="px-2">...</span>}

                {currentPage > 1 && (
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="w-10 h-10 p-0"
                  >
                    {currentPage - 1}
                  </Button>
                )}

                <Button
                  variant="default"
                  className="w-10 h-10 p-0"
                >
                  {currentPage}
                </Button>

                {currentPage < totalPages && (
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="w-10 h-10 p-0"
                  >
                    {currentPage + 1}
                  </Button>
                )}

                {currentPage < totalPages - 2 && <span className="px-2">...</span>}

                {currentPage < totalPages - 1 && (
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(totalPages)}
                    className="w-10 h-10 p-0"
                  >
                    {totalPages}
                  </Button>
                )}
              </div>

              <Button
                variant="outline"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="hidden md:flex items-center gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Mobile Pagination */}
              <div className="flex md:hidden items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-10 h-10 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}